import type { BrandConfig } from "./brand-config"
import { generatePalette } from "./color-palette"

/**
 * Generates a CSS string that overrides the default brand colors with custom ones.
 * Returns empty string if no custom colors are configured (i.e., using Shelfi defaults).
 */
export function generateBrandCss(config: BrandConfig): string {
  if (!config.isCustomBranded) return ""

  const lightPrimary = generatePalette(config.colors.light.primary)
  const lightSecondary = generatePalette(config.colors.light.secondary)
  const lightAccent = generatePalette(config.colors.light.accent)

  const darkPrimary = generatePalette(config.colors.dark.primary)
  const darkSecondary = generatePalette(config.colors.dark.secondary)
  const darkAccent = generatePalette(config.colors.dark.accent)

  return `
/* Brand color overrides - generated from env vars */
:root {
  /* Primary palette (replaces brand-darkblue) */
  --color-brand-darkblue-50: ${lightPrimary[50]};
  --color-brand-darkblue-150: ${lightPrimary[150]};
  --color-brand-darkblue-300: ${lightPrimary[300]};
  --color-brand-darkblue-500: ${lightPrimary[500]};
  --color-brand-darkblue-700: ${lightPrimary[700]};
  --color-brand-darkblue-900: ${lightPrimary[900]};

  /* Secondary palette (replaces brand-cyan) */
  --color-brand-cyan-50: ${lightSecondary[50]};
  --color-brand-cyan-150: ${lightSecondary[150]};
  --color-brand-cyan-300: ${lightSecondary[300]};
  --color-brand-cyan-500: ${lightSecondary[500]};
  --color-brand-cyan-700: ${lightSecondary[700]};
  --color-brand-cyan-900: ${lightSecondary[900]};

  /* Accent palette (replaces brand-orange) */
  --color-brand-orange-50: ${lightAccent[50]};
  --color-brand-orange-150: ${lightAccent[150]};
  --color-brand-orange-300: ${lightAccent[300]};
  --color-brand-orange-500: ${lightAccent[500]};
  --color-brand-orange-700: ${lightAccent[700]};
  --color-brand-orange-900: ${lightAccent[900]};

  /* Semantic token overrides */
  --primary: ${lightPrimary[500]};
  --primary-foreground: ${config.colors.light.primaryForeground ?? "var(--color-neutral-0)"};
  --secondary: ${lightSecondary[500]};
  --secondary-foreground: ${config.colors.light.secondaryForeground ?? "var(--color-neutral-0)"};
  --accent: ${lightAccent[500]};
  --ring: ${lightPrimary[300]};
  --sidebar-primary: ${lightPrimary[500]};
  --sidebar-ring: ${lightPrimary[300]};
}

.dark {
  /* Primary palette dark mode */
  --color-brand-darkblue-50: ${darkPrimary[50]};
  --color-brand-darkblue-150: ${darkPrimary[150]};
  --color-brand-darkblue-300: ${darkPrimary[300]};
  --color-brand-darkblue-500: ${darkPrimary[500]};
  --color-brand-darkblue-700: ${darkPrimary[700]};
  --color-brand-darkblue-900: ${darkPrimary[900]};

  /* Secondary palette dark mode */
  --color-brand-cyan-50: ${darkSecondary[50]};
  --color-brand-cyan-150: ${darkSecondary[150]};
  --color-brand-cyan-300: ${darkSecondary[300]};
  --color-brand-cyan-500: ${darkSecondary[500]};
  --color-brand-cyan-700: ${darkSecondary[700]};
  --color-brand-cyan-900: ${darkSecondary[900]};

  /* Accent palette dark mode */
  --color-brand-orange-50: ${darkAccent[50]};
  --color-brand-orange-150: ${darkAccent[150]};
  --color-brand-orange-300: ${darkAccent[300]};
  --color-brand-orange-500: ${darkAccent[500]};
  --color-brand-orange-700: ${darkAccent[700]};
  --color-brand-orange-900: ${darkAccent[900]};

  /* Semantic token overrides dark */
  --primary: ${darkPrimary[300]};
  --primary-foreground: ${config.colors.dark.primaryForeground ?? "var(--color-neutral-0)"};
  --secondary: ${darkSecondary[300]};
  --secondary-foreground: ${config.colors.dark.secondaryForeground ?? "var(--color-neutral-0)"};
  --accent: ${darkAccent[300]};
  --ring: ${darkPrimary[150]};
  --sidebar-primary: ${darkPrimary[300]};
  --sidebar-ring: ${darkPrimary[150]};
}
`.trim()
}
